import * as React from 'react'
import { Box, Button, Paper, TextField, Typography } from '@mui/material'
import styled from '@emotion/styled'
import firebase from 'infrastructure/firebase'
import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  importedSheetDataAtom,
  sheetImportErrorsAtom,
  sheetIdAtom,
  refreshCounterAtom,
} from './atoms'
import { ImportErrorDisplay } from './ImportErrorDisplay'
import { SummaryTable } from '../../SummaryTable'
import { useImportStandardsFromSheet, useImportSummary } from './hooks'
import { OpenJsonButton } from '../../utility/OpenJsonButton'
import { DownloadJsonButton } from '../../utility/DownloadJsonButton'

const SectionPaper = styled(Paper)(() => ({
  padding: '24px',
  marginBottom: '24px',
}))

interface ImportFormContentProps {
  treeId: string
  versionId: string
}

function ImportFormContent({ treeId, versionId }: ImportFormContentProps) {
  const sheetId = useAtomValue(sheetIdAtom)
  const setSheetId = useSetAtom(sheetIdAtom)
  const guser = useAtomValue(googleUserAtom)
  const importedSheetData = useAtomValue(importedSheetDataAtom)
  const importErrors = useAtomValue(sheetImportErrorsAtom)
  const refreshCounter = useAtomValue(refreshCounterAtom)
  const syncStarted = refreshCounter > 0
  const { importFromSheet, loading } = useImportStandardsFromSheet(
    treeId,
    versionId,
  )

  const { summaryRows, serializableData } = useImportSummary()

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        label="Google Sheet ID"
        value={sheetId}
        onChange={(e) => setSheetId(e.target.value)}
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        onClick={() => {
          importFromSheet(sheetId)
        }}
        disabled={!sheetId.trim() || loading || syncStarted}
      >
        {loading ? 'Importing...' : 'Scan Sheet'}
      </Button>

      {importErrors.length > 0 && <ImportErrorDisplay />}

      {importedSheetData &&
        importedSheetData.nodeList &&
        importedSheetData.nodeList.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1">Summary</Typography>
              {serializableData && (
                <>
                  <OpenJsonButton
                    data={serializableData}
                    disabled={
                      !importedSheetData.nodeList ||
                      importedSheetData.nodeList.length === 0
                    }
                    buttonText="Open JSON"
                  />
                  <DownloadJsonButton
                    data={serializableData}
                    filename={`imported_sheet_data_${treeId}_${versionId}.json`}
                    disabled={
                      !importedSheetData.nodeList ||
                      importedSheetData.nodeList.length === 0
                    }
                  />
                </>
              )}
            </Box>
            <SummaryTable rows={summaryRows} />
          </Box>
        )}
    </Box>
  )
}

interface ImportStandardsFromSheetProps {
  treeId: string
  versionId: string
}

export function ImportStandardsFromSheet({
  treeId,
  versionId,
}: ImportStandardsFromSheetProps) {
  return (
    <SectionPaper>
      <Typography variant="h6" gutterBottom>
        Set Working Google Sheet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add or remove standards from existing skill lists in the sheet. To
        create a new skills list, set the node_id field to{' '}
        <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>NEW</span>.
      </Typography>
      <GoogleLoginButton
        hideWhenLoggedIn={true}
        ParentComponent={({ children }) => (
          <Box sx={{ mt: 1, display: 'inline-block' }}>{children}</Box>
        )}
        LoggedInComponent={({ user: _user }: { user: firebase.User }) => (
          <ImportFormContent treeId={treeId} versionId={versionId} />
        )}
      />
    </SectionPaper>
  )
}
